import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, Plus, Loader2, Edit2, Trash2, AlertCircle,
  ToggleLeft, ToggleRight, CheckCircle2, Clock, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import PartnerLayout from '../../components/partner/PartnerLayout';

const STATUS_STYLES = {
  pending: { 
    bg: 'bg-yellow-500/10', 
    text: 'text-yellow-500', 
    border: 'border-yellow-500/20',
    label: 'Pendiente',
    icon: Clock
  },
  approved: { 
    bg: 'bg-green-500/10', 
    text: 'text-green-500', 
    border: 'border-green-500/20',
    label: 'Aprobado',
    icon: CheckCircle2
  },
  rejected: { 
    bg: 'bg-red-500/10', 
    text: 'text-red-500', 
    border: 'border-red-500/20',
    label: 'Rechazado',
    icon: XCircle
  }
};

const PartnerBenefitsPage = () => {
  const navigate = useNavigate();
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redemptions, setRedemptions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const [benefitsRes, redemptionsRes] = await Promise.all([
        api.get('/partners/me/benefits', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/partners/redemptions', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBenefits(benefitsRes.data || []);
      setRedemptions(redemptionsRes.data?.redemptions || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar beneficios');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBenefit = async (benefitId) => {
    if (!window.confirm('¿Estás seguro de eliminar este beneficio?')) return;
    
    try {
      const token = localStorage.getItem('partner_token');
      await api.delete(`/partners/me/benefits/${benefitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Beneficio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar beneficio');
    }
  };

  const handleToggleBenefit = async (benefitId, currentStatus) => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.put(`/partners/me/benefits/${benefitId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.is_active ? 'Beneficio activado' : 'Beneficio desactivado');
        setBenefits(prevBenefits => 
          prevBenefits.map(b => 
            b.id === benefitId ? { ...b, is_active: response.data.is_active } : b
          )
        );
      } else {
        toast.error('Error al cambiar estado del beneficio');
      }
    } catch (error) {
      console.error('Toggle error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Error al cambiar estado del beneficio');
    }
  };

  const approvedCount = benefits.filter(b => b.status === 'approved').length;
  const pendingCount = benefits.filter(b => b.status === 'pending').length;
  const rejectedCount = benefits.filter(b => b.status === 'rejected').length;

  if (loading) {
    return (
      <PartnerLayout currentPage="beneficios">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout currentPage="beneficios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-black text-white">Mis Beneficios</h1>
          <p className="text-zinc-400">Gestiona los beneficios que ofreces a suscriptores BRAVX</p>
        </div>
        <Link to="/partner/crear-beneficio">
          <Button 
            className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            data-testid="new-benefit-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Beneficio
          </Button>
        </Link>
      </div>

      {/* Info Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 flex items-start gap-4 mb-6"
      >
        <AlertCircle size={22} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[#D4AF37] font-semibold">Los beneficios requieren aprobación</p>
          <p className="text-zinc-400 text-sm">Todos los beneficios deben ser aprobados por un administrador antes de aparecer en FULL BRAVX.</p>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-[#D4AF37]">{approvedCount}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Activos</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-yellow-500">{pendingCount}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Pendientes</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-white">{benefits.length}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Totales</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-white">{redemptions.length}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Canjeados</p>
        </div>
      </div>

      {/* Benefits List */}
      {benefits.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center"
        >
          <Gift size={56} className="mx-auto text-zinc-700 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No tenés beneficios</h3>
          <p className="text-zinc-500 mb-6">Creá tu primer beneficio para atraer clientes BRAVX</p>
          <Link to="/partner/crear-beneficio">
            <Button 
              className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold"
            >
              <Plus size={18} className="mr-2" />
              Crear Beneficio
            </Button>
          </Link>
        </motion.div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((benefit, index) => {
            const status = STATUS_STYLES[benefit.status] || STATUS_STYLES.pending;
            const StatusIcon = status.icon;
            return (
              <motion.div
                key={benefit.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors"
                data-testid={`benefit-card-${benefit.id}`}
              >
                {benefit.image_url && (
                  <div className="h-36 bg-zinc-800 overflow-hidden">
                    <img src={benefit.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-bold text-white text-lg leading-tight">{benefit.title}</h3>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase flex items-center gap-1 ${status.bg} ${status.text} border ${status.border}`}>
                      <StatusIcon size={12} />
                      {status.label}
                    </span>
                  </div>
                  
                  <p className="text-zinc-400 text-sm line-clamp-2 mb-4">{benefit.description}</p>
                  
                  {/* Usage Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-zinc-800/50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#D4AF37]">{benefit.codes_generated || 0}</p>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Generados</p>
                    </div>
                    <div className="text-center border-x border-zinc-700">
                      <p className="text-xl font-bold text-green-400">{benefit.codes_redeemed || 0}</p>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Canjeados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">
                        {benefit.limited_coupons === 0 || !benefit.limited_coupons 
                          ? '∞' 
                          : `${benefit.remaining_coupons ?? benefit.limited_coupons}/${benefit.limited_coupons}`
                        }
                      </p>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
                        {benefit.limited_coupons > 0 ? 'Disponibles' : 'Stock'}
                      </p>
                    </div>
                  </div>

                  {benefit.status === 'rejected' && benefit.rejection_reason && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                      <p className="text-red-400 text-xs"><span className="font-bold">Motivo:</span> {benefit.rejection_reason}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* Toggle Active/Inactive - only for approved benefits */}
                    {benefit.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleBenefit(benefit.id, benefit.is_active)}
                        className={`${benefit.is_active 
                          ? 'text-green-400 hover:bg-green-500/10' 
                          : 'text-zinc-500 hover:bg-zinc-700'
                        }`}
                        title={benefit.is_active ? 'Desactivar' : 'Activar'}
                        data-testid={`toggle-btn-${benefit.id}`}
                      >
                        {benefit.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </Button>
                    )}
                    
                    {/* Edit Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/partner/editar-beneficio/${benefit.id}`)}
                      className="flex-1 border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
                      data-testid={`edit-btn-${benefit.id}`}
                    >
                      <Edit2 size={14} className="mr-1" />
                      Editar
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteBenefit(benefit.id)}
                      className="text-red-400 hover:bg-red-500/10"
                      title="Eliminar"
                      data-testid={`delete-btn-${benefit.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerBenefitsPage;
